import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "@/hooks/useSmoothScroll";
import { CAL_LINK } from "@/lib/booking";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskLine = ({ children, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      variants={lineVariant}
      custom={i}
      initial="hidden"
      animate="show"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const ridgeY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const ridgeFrontY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden min-h-[92vh] flex items-center pt-28 pb-24 sm:pt-32"
    >
      {/* layered parallax ridges */}
      <motion.div
        style={{ y: ridgeY }}
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10"
      >
        <svg viewBox="0 0 1440 420" className="w-full h-auto" preserveAspectRatio="xMidYMax slice">
          <path
            fill="#16273F"
            fillOpacity="0.06"
            d="M0 420 L300 170 L430 260 L560 120 L690 250 L820 60 L980 240 L1120 150 L1280 250 L1440 130 L1440 420 Z"
          />
        </svg>
      </motion.div>
      <motion.div
        style={{ y: ridgeFrontY }}
        className="pointer-events-none absolute inset-x-0 bottom-0 -z-10"
      >
        <svg viewBox="0 0 1440 320" className="w-full h-auto" preserveAspectRatio="xMidYMax slice">
          <path
            fill="#9A8420"
            fillOpacity="0.08"
            d="M0 320 L360 150 L520 250 L680 110 L860 260 L1040 140 L1220 250 L1440 160 L1440 320 Z"
          />
        </svg>
      </motion.div>
      <div className="pointer-events-none absolute -top-24 right-0 -z-10 h-96 w-96 rounded-full bg-[#EDE6C9]/40 blur-3xl" />

      <motion.div style={{ y: textY, opacity: fade }} className="w-full">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-[#9A8420]" />
            <span className="eyebrow text-[#9A8420]">
              Automatisierung · Rhein-Neckar-Region
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            style={{ fontFamily: "Manrope, sans-serif" }}
            className="mt-7 font-extrabold tracking-tight text-[#16273F] text-5xl sm:text-7xl lg:text-8xl leading-[0.98]"
          >
            <MaskLine i={0}>Kein verpasster</MaskLine>
            <MaskLine i={1}>
              Anruf. <span className="text-[#9A8420]">Kein</span>
            </MaskLine>
            <MaskLine i={2}>Termin-Chaos.</MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            data-testid="hero-subtitle"
            className="mt-8 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Wir richten für Handwerksbetriebe, Praxen und Makler einfache
            Automatisierungen ein: Termine buchen sich von selbst, Anfragen landen
            sofort bei Ihnen, und Ihre Website antwortet rund um die Uhr — ohne dass
            Sie etwas dazulernen müssen.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              href={CAL_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-primary-cta"
              className="group relative inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full bg-[#16273F] text-white text-base font-semibold overflow-hidden transition-transform hover:-translate-y-0.5"
            >
              <span className="absolute inset-0 -translate-x-full bg-[#9A8420] transition-transform duration-500 group-hover:translate-x-0" />
              <span className="relative z-10">Kostenloses Erstgespräch anfragen</span>
            </a>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("#leistungen")}
              className="inline-flex items-center justify-center h-14 px-8 rounded-full border border-[#16273F]/25 text-[#16273F] text-base font-semibold hover:border-[#16273F] transition-colors"
            >
              Was wir machen
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.7 }}
            className="mt-4"
          >
            <button
              type="button"
              data-testid="hero-no-appointment-link"
              onClick={() => scrollToId("#kontakt")}
              className="text-sm text-slate-500 hover:text-[#16273F] underline underline-offset-2 transition-colors"
            >
              Noch keinen Termin? Lieber erst kurz schreiben.
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-16 flex items-center gap-2 text-sm text-slate-400"
          >
            <ArrowDown className="w-4 h-4 animate-bounce" />
            Zwei Ansprechpartner · Klare Sprache · Unverbindlich
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
